// Strukturovaná data pro Google (schema.org) na jednom místě.
// Používá je jak build (scripts/create-route-fallbacks.mjs), tak běhový SEO
// kód v App.jsx, aby se statické a klientské JSON-LD nikdy nerozešly.

export const SITE_ORIGIN = 'https://www.norisknodrift.com';
export const BRAND_NAME = 'NO RISK NO DRIFT';

// Obchodní podmínky prodejce, které Google vyžaduje u nabídek.
// Hodnoty odpovídají českému právu (14denní odstoupení od smlouvy).
export const MERCHANT_POLICY = {
  currency: 'CZK',
  country: 'CZ',
  returnDays: 14,
  // Zpětné poštovné hradí zákazník (§ 1820 OZ) — Google u ReturnShippingFees
  // vyžaduje kladnou částku, proto uvádíme nejnižší reálnou sazbu dopravce.
  returnShippingFee: 89,
  // výchozí sazba, pokud se nepodaří načíst skutečné dopravy z API
  fallbackShipping: [
    { name: 'Parcel box', price: 89, days: [1, 2] },
    { name: 'Home delivery', price: 119, days: [1, 3] },
    { name: 'Personal pickup', price: 0, days: [0, 1] }
  ]
};

const absolute = value => (!value ? '' : value.startsWith('http') ? value : `${SITE_ORIGIN}${value}`);

// Adresa místa konání — Google chce strukturovanou PostalAddress, ne jen text.
const COUNTRY_CODES = {
  'czech republic': 'CZ', czechia: 'CZ', latvia: 'LV', germany: 'DE', poland: 'PL',
  usa: 'US', 'united states': 'US', slovakia: 'SK', austria: 'AT', hungary: 'HU',
  netherlands: 'NL', france: 'FR', italy: 'IT', spain: 'ES', sweden: 'SE', finland: 'FI',
  norway: 'NO', denmark: 'DK', lithuania: 'LT', estonia: 'EE', belgium: 'BE',
  switzerland: 'CH', 'united kingdom': 'GB'
};

export function placeAddress(place) {
  const parts = String(place || '').split(',').map(part => part.trim()).filter(Boolean);
  if (!parts.length) return undefined;
  const countryName = parts[parts.length - 1];
  const address = {
    '@type': 'PostalAddress',
    addressLocality: parts[0],
    addressCountry: COUNTRY_CODES[countryName.toLowerCase()] || countryName
  };
  if (parts.length > 2) address.addressRegion = parts[1];
  return address;
}

// Z adresy pořadatele odvodíme jeho jméno — Google vyžaduje organizer.name.
const ORGANISER_NAMES = {
  'driftmasters.gp': 'Drift Masters',
  'formulad.com': 'Formula DRIFT',
  'irondriftking.de': 'Iron Drift King'
};

export function organiserFrom(url) {
  if (!url) return undefined;
  let host = '';
  try {
    host = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    host = String(url).replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
  const name = ORGANISER_NAMES[host]
    || host.split('.')[0].replace(/[-_]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
  return { '@type': 'Organization', name, url };
}

// Dodací podmínky nabídky. `methods` jsou skutečné dopravy z API; bez nich
// se použije výchozí sazebník, ať pole nikdy nechybí.
export function shippingDetails(methods) {
  const list = (methods && methods.length ? methods : MERCHANT_POLICY.fallbackShipping)
    .map(method => ({
      name: method.name,
      price: Number(method.price) || 0,
      days: method.days || [1, 3]
    }));
  return list.map(method => ({
    '@type': 'OfferShippingDetails',
    name: method.name,
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: method.price,
      currency: MERCHANT_POLICY.currency
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: MERCHANT_POLICY.country
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
      transitTime: { '@type': 'QuantitativeValue', minValue: method.days[0], maxValue: method.days[1], unitCode: 'DAY' }
    }
  }));
}

export function returnPolicy() {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: MERCHANT_POLICY.country,
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: MERCHANT_POLICY.returnDays,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/ReturnShippingFees',
    returnShippingFeesAmount: {
      '@type': 'MonetaryAmount',
      value: MERCHANT_POLICY.returnShippingFee,
      currency: MERCHANT_POLICY.currency
    },
    merchantReturnLink: `${SITE_ORIGIN}/terms`
  };
}

// Licenční údaje k fotkám — Google Images je vyžaduje u ImageObject.
export function imageLicence() {
  return {
    creator: { '@type': 'Organization', name: BRAND_NAME, url: `${SITE_ORIGIN}/` },
    copyrightNotice: `© ${new Date().getFullYear()} ${BRAND_NAME}`,
    creditText: BRAND_NAME,
    license: `${SITE_ORIGIN}/terms`,
    acquireLicensePage: `${SITE_ORIGIN}/#contact`
  };
}

// Platnost nabídky. Google chce u nabídky i `validFrom`; datum musí být stabilní
// (ne čas buildu) a v minulosti, jinak by nabídka vypadala jako budoucí.
export function priceValidity() {
  const year = new Date().getFullYear();
  return { validFrom: `${year}-01-01`, priceValidUntil: `${year + 1}-12-31` };
}

export function productSchema(item, canonical, methods) {
  const price = Number(item.price) || 0;
  // Bez ceny nemůžeme vytvořit nabídku a Product bez offers Google odmítá —
  // takové položky proto strukturovaná data nedostávají vůbec.
  if (price <= 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: item.name,
    image: [absolute(item.image)].filter(Boolean),
    description: item.description,
    sku: item.sku || item.slug,
    brand: { '@type': 'Brand', name: BRAND_NAME },
    url: canonical,
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency: MERCHANT_POLICY.currency,
      availability: Number(item.stock) > 0 || item.official
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      url: canonical,
      ...priceValidity(),
      seller: { '@type': 'Organization', name: BRAND_NAME, url: `${SITE_ORIGIN}/` },
      shippingDetails: shippingDetails(methods),
      hasMerchantReturnPolicy: returnPolicy()
    }
  };
}

export function eventSchema(item, canonical) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: item.name,
    startDate: item.isoDate,
    endDate: item.isoEndDate || item.isoDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: item.place,
      address: placeAddress(item.place)
    },
    image: [absolute(item.image)].filter(Boolean),
    description: item.description,
    organizer: organiserFrom(item.organiser),
    performer: [
      { '@type': 'Organization', name: BRAND_NAME, url: `${SITE_ORIGIN}/` }
    ],
    // Vstupenky prodává pořadatel — odkazujeme na jeho web, cenu si neurčujeme.
    offers: item.organiser ? {
      '@type': 'Offer',
      url: item.organiser,
      availability: 'https://schema.org/InStock',
      validFrom: item.isoDate
    } : undefined,
    url: canonical
  };
}

export function imageSchema(item, canonical) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    name: item.title || item.name,
    description: item.caption || item.description,
    contentUrl: absolute(item.src || item.image),
    url: canonical,
    ...imageLicence()
  };
}
